import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, Check, X, Plane, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, PersonAvatar, EmptyState } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, fmtDate, localToday } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";
import { useAuth, isManager } from "@/context/AuthContext";

const TYPES = ["vacation", "sick", "personal", "parental", "unpaid", "other"];

export default function Leave() {
  const { user } = useAuth();
  const manager = isManager(user);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { employees } = useEmployees();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("pending");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [f, setF] = useState({ employee_id: "", leave_type: "vacation", start_date: localToday(), end_date: localToday(), reason: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/leave"); setItems(data); } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.end_date < f.start_date) return toast.error("End date must be on or after start date");
    setBusy("new");
    try {
      await api.post("/leave", { ...f, employee_id: f.employee_id || null });
      toast.success("Leave request submitted");
      setOpen(false);
      setF({ ...f, reason: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(null); }
  };

  const decide = async (l, decision) => {
    setBusy(l.leave_id + decision);
    try { await api.post(`/leave/${l.leave_id}/decide`, { decision }); toast.success(`Leave ${decision}`); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(null); }
  };

  const cancel = async (l) => {
    try { await api.delete(`/leave/${l.leave_id}`); toast.success("Request cancelled"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const filtered = (items || []).filter((l) => tab === "all" || l.status === tab);
  const pendingCount = (items || []).filter((l) => l.status === "pending").length;

  return (
    <div data-testid="leave-page">
      <PageHeader title="Leave" subtitle={manager ? "Requests from your team" : "Your leave requests"}
        actions={<>
          {manager && pendingCount > 0 && <Button variant="secondary" onClick={() => navigate("/ai-ceo?q=Review pending leave requests against our calendar and deadlines, and propose decisions")} data-testid="leave-ask-ceo-button"><Sparkles className="h-4 w-4 mr-1.5 text-primary" />Get CEO recommendation</Button>}
          <Button onClick={() => setOpen(true)} data-testid="leave-request-new-button"><Plus className="h-4 w-4 mr-1.5" />Request leave</Button>
        </>} />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="leave-tab-pending">Pending {pendingCount > 0 && <span className="ml-1.5 text-xs bg-[hsl(var(--warning))] rounded-full px-1.5">{pendingCount}</span>}</TabsTrigger>
          <TabsTrigger value="approved" data-testid="leave-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="leave-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="leave-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-card border border-border rounded-xl">
        {items === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div> :
          filtered.length === 0 ? <EmptyState icon={Plane} title="No leave requests here" /> : (
            <ul className="divide-y divide-border">
              {filtered.map((l) => (
                <li key={l.leave_id} className="p-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="leave-request-item">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <PersonAvatar name={l.employee_name} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{l.employee_name} <span className="text-muted-foreground font-normal capitalize">· {l.leave_type}</span></p>
                      <p className="text-xs text-muted-foreground">{fmtDate(l.start_date, { weekday: "short", month: "short", day: "numeric" })} → {fmtDate(l.end_date, { weekday: "short", month: "short", day: "numeric" })} · {l.days} day{l.days > 1 ? "s" : ""}</p>
                      {l.reason && <p className="text-xs text-muted-foreground mt-0.5 truncate">“{l.reason}”</p>}
                      {l.decided_by && <p className="text-xs text-muted-foreground mt-0.5">Decided by {l.decided_by}{l.decision_note ? ` · ${l.decision_note}` : ""}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={l.status} />
                    {l.status === "pending" && manager && (
                      <>
                        <Button size="sm" onClick={() => decide(l, "approved")} disabled={!!busy} data-testid="leave-request-approve-button">{busy === l.leave_id + "approved" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Approve</Button>
                        <Button size="sm" variant="secondary" onClick={() => decide(l, "rejected")} disabled={!!busy} data-testid="leave-request-reject-button"><X className="h-4 w-4 mr-1" />Reject</Button>
                      </>
                    )}
                    {l.status === "pending" && (l.created_by === user.user_id) && !manager && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(l)} data-testid="leave-request-cancel-button">Cancel</Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="leave-request-dialog">
          <DialogHeader><DialogTitle>Request leave</DialogTitle><DialogDescription>{manager ? "Request for yourself or on behalf of a team member." : "Your manager will review it."}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {manager && (
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <Select value={f.employee_id || "me"} onValueChange={(v) => setF({ ...f, employee_id: v === "me" ? "" : v })}>
                  <SelectTrigger data-testid="leave-employee-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="me">Myself</SelectItem>{employees.filter((e) => e.user_id !== user.user_id).map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={f.leave_type} onValueChange={(v) => setF({ ...f, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From</Label><DatePicker value={f.start_date} onChange={(v) => setF({ ...f, start_date: v, end_date: f.end_date < v ? v : f.end_date })} testid="leave-start-date-picker" /></div>
              <div className="space-y-1.5"><Label>To</Label><DatePicker value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testid="leave-end-date-picker" /></div>
            </div>
            <div className="space-y-1.5"><Label>Reason</Label><Textarea rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="leave-reason-input" /></div>
            <DialogFooter><Button type="submit" disabled={busy === "new"} data-testid="leave-submit-button">{busy === "new" && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Submit request</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
